import { useRetry } from '@rpldy/retry-hooks'
import {
  useAbortItem,
  useBatchAddListener,
  useItemErrorListener,
  useItemFinishListener,
  useItemProgressListener,
  useRequestPreSend,
  useUploady,
} from '@rpldy/uploady'
import isEmpty from 'lodash/isEmpty'
import { useCallback, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { useDeleteAttachment } from '@features/invoices/hooks/attachments/useDeleteAttachment'

import { AttachmentStatus } from '../enums/attachmentStatus'
import { AttachmentUploadErrorType } from '../enums/attachmentUploadErrorType'
import { Attachment } from '../types/attachment'
import { getIsFileSizeExceeded } from '../utils/getIsFileSizeExceeded'

export const useAttachmentsUpload = () => {
  const uploady = useUploady()
  const abortItem = useAbortItem()
  const retryItem = useRetry()
  const { delete: deleteItem, isProcessing: isDeleting } = useDeleteAttachment()
  const [uploads, setUploads] = useState<Record<string, Attachment>>({})

  const sessionId = uploady.getOptions().destination?.params?.sessionId as string

  // qquuid is a prop required by backend for each uploaded file
  useRequestPreSend(({ items, options }) => {
    options.params = { qquuid: uuidv4() }

    return { items, options }
  })

  useBatchAddListener((batch) => {
    const newUploads: Record<string, Attachment> = {}

    for (const item of batch.items) {
      const isSizeExceeded = getIsFileSizeExceeded(item.file.size)

      if (isSizeExceeded) {
        uploady.abort(item.id)
      }

      newUploads[item.id] = {
        id: item.id,
        fileName: item.file.name,
        fileSize: item.file.size,
        progress: 0,
        uploadedBytes: 0,
        isRetryable: false,
        status: isSizeExceeded ? AttachmentStatus.Failed : AttachmentStatus.Uploading,
        ...(isSizeExceeded && {
          errorType: AttachmentUploadErrorType.Size,
        }),
      }
    }

    if (!isEmpty(newUploads)) {
      setUploads((prev) => ({ ...prev, ...newUploads }))
    }
  })

  const updateUploadItem = useCallback(
    (id: string, patch: Partial<Attachment>) =>
      setUploads((prev) => {
        const current = prev[id]
        if (!current) {
          return prev
        }

        return { ...prev, [id]: { ...current, ...patch } }
      }),
    [setUploads],
  )

  const removeUploadItem = useCallback(
    (id: string) =>
      setUploads((prev) => {
        const { [id]: removed, ...rest } = prev

        return rest
      }),
    [setUploads],
  )

  useItemProgressListener((item) => {
    updateUploadItem(item.id, {
      progress: item.completed,
      uploadedBytes: item.loaded,
    })
  })

  useItemFinishListener((item) => {
    updateUploadItem(item.id, {
      status: AttachmentStatus.Done,
      progress: 100,
      isRetryable: false,
      uploadedBytes: item.loaded,
      uuid: item.uploadResponse.data.uuid,
    })
  })

  useItemErrorListener((item) => {
    updateUploadItem(item.id, {
      errorType: AttachmentUploadErrorType.General,
      isRetryable: true,
      status: AttachmentStatus.Failed,
    })
  })

  const retryUpload = useCallback((id: string) => retryItem(id), [retryItem])

  const cancelUpload = useCallback(
    (id: string) => {
      abortItem(id)
      removeUploadItem(id)
    },
    [abortItem, removeUploadItem],
  )

  const removeAttachment = useCallback(
    (id: string) => {
      const attachment = uploads[id]
      if (!attachment) {
        return
      }

      const uuid = attachment.uuid
      if (uuid) {
        deleteItem({ sessionId, uuid })
      }
      removeUploadItem(id)
    },
    [deleteItem, sessionId, removeUploadItem, uploads],
  )

  const attachments = Object.values(uploads)
  const attachmentUuids = attachments.map((attachment) => attachment.uuid).filter((uuid): uuid is string => !!uuid)
  const isUploading = attachments.some((attachment) => attachment.status === AttachmentStatus.Uploading)
  const hasFailedAttachments = attachments.some((attachment) => attachment.status === AttachmentStatus.Failed)

  return {
    attachments,
    attachmentUuids,
    cancelUpload,
    hasFailedAttachments,
    isDeleting,
    isUploading,
    removeAttachment,
    retryUpload,
  }
}
